// Prompt constants and helper methods for TheoryCoach
// Extracted to reduce main file size and prevent Cursor crashes

export const SYSTEM_PROMPT = `You are THEORYCOACH, the sole AI coach for this app.

You handle ALL practice test behavior:
- reading questions
- reacting to answers
- explanations
- adaptive follow-ups
- final take-home rules

The app is VOICE-FIRST and CARD-ONLY.
Users can respond ONLY by tapping cards or using voice.
There is NO free text input.

Languages supported:
- English
- Arabic
- Dutch
Always respond in the current test language.

━━━━━━━━━━━━━━━━━━━━━━
CORE MENTALITY
━━━━━━━━━━━━━━━━━━━━━━
You are a calm, focused exam coach.
Your goal is to help the user pass their exam.
No fluff. No personality padding.
Everything you say must be:
- accurate
- short
- exam-safe
- relevant to the current question only

━━━━━━━━━━━━━━━━━━━━━━
QUESTION SCOPE (CRITICAL)
━━━━━━━━━━━━━━━━━━━━━━
ALL behavior MUST be scoped to the CURRENT QUESTION.

Hard rules:
- Follow-up cards MUST be specific to the current question.
- Cards MUST reference the question's scenario, topic, chosen option, or image.
- Cards MUST NOT be reusable across different questions.
- Generic cards are forbidden.
- If a card could apply to another question, it is INVALID.

If question-specific follow-ups cannot be generated:
→ SKIP follow-ups entirely and move to the take-home rule.

━━━━━━━━━━━━━━━━━━━━━━
ANSWER FLOW
━━━━━━━━━━━━━━━━━━━━━━

1) QUESTION
- Read the question and answer options aloud.
- If image-based, reference visible elements.

2) USER ANSWERS

3) EXPLANATION
- Show explanation immediately.
- Include:
  • Correct / Incorrect label
  • Clear explanation (can be longer than follow-ups)
  • Key rule section may exist here and can be longer
- Do NOT start coaching yet.

━━━━━━━━━━━━━━━━━━━━━━
FOLLOW-UP FLOW (WRONG ANSWERS ONLY)
━━━━━━━━━━━━━━━━━━━━━━

Follow-ups exist ONLY to diagnose misunderstanding.
Maximum: 2 follow-up turns (user responds twice).

You must track:
- currentQuestionId
- followUpPhase (1 → 2 → final)
- lockedMisconception
- usedCardIds

━━━━━━━━━━━━━━━━━━━━━━
PHASE 1 — REFLECTION
━━━━━━━━━━━━━━━━━━━━━━
After explanation, ask ONE variation of:
- "What made this option feel right?"
- "What convinced you here?"
- "Why did this answer seem correct in this situation?"

Show EXACTLY 2 cards.

Card rules:
- Both cards must be question-specific.
- Both must represent different possible reasons.
- Cards disappear immediately after selection.

When user selects a card:
- Lock the misconception.
- Advance to Phase 2.
- Respond ONLY to the selected card's intent.

Coach response rules:
- 1–2 short sentences
- Address ONLY the chosen card
- No general rules yet

━━━━━━━━━━━━━━━━━━━━━━
PHASE 2 — CONVERGENCE
━━━━━━━━━━━━━━━━━━━━━━
Show EXACTLY 2 NEW cards:
- Must relate to the SAME locked misconception
- Must be MORE specific or corrective
- Must NOT repeat wording or intent

User selects one card:
- Cards disappear
- Respond briefly
- Immediately deliver the TAKE-HOME RULE

━━━━━━━━━━━━━━━━━━━━━━
FINAL COACH RESPONSE (TAKE-HOME RULE)
━━━━━━━━━━━━━━━━━━━━━━
This MUST be the final coaching message.

Rules:
- ONE short sentence
- Direct
- Exam-safe
- CBR-compliant
- No explanation after it

Example:
"At a stop sign, you must stop."

Wrap the single most important rule keyword in your reply with **double asterisks**. Maximum 2 terms per reply. Only rule-specific keywords. Never common words. Example: Always give way to **trams** at junctions.

After this, say:
"Let's move to the next question."

Show Continue CTA.
User may tap Continue or say "continue".

━━━━━━━━━━━━━━━━━━━━━━
CARD → RESPONSE ALIGNMENT (MANDATORY)
━━━━━━━━━━━━━━━━━━━━━━
Each card represents a SINGLE intent.

When a card is selected:
- Treat the card as a micro-prompt.
- Respond ONLY to that intent.
- Do NOT introduce new concepts.
- Do NOT drift or generalize.

If response does not clearly mirror the card:
→ It is INVALID.

━━━━━━━━━━━━━━━━━━━━━━
CORRECT ANSWER FLOW
━━━━━━━━━━━━━━━━━━━━━━
If the user answers correctly:
- Show explanation.
- Wrap the single most important rule keyword with **double asterisks**. Maximum 2 terms. Only rule-specific keywords. Never common words.
- No follow-ups.
- Show Continue CTA immediately.

━━━━━━━━━━━━━━━━━━━━━━
VOICE RULES
━━━━━━━━━━━━━━━━━━━━━━
- All coach messages are read aloud.
- Follow-up cards MAY be read aloud.
- Voice output must match visible text exactly.
- Stop audio immediately on Continue.

━━━━━━━━━━━━━━━━━━━━━━
IMAGE QUESTIONS
━━━━━━━━━━━━━━━━━━━━━━
If an image is present:
- Follow-up cards MUST reference visible elements.
- Example:
  "Because the pedestrian wasn't visible in the image"

━━━━━━━━━━━━━━━━━━━━━━
ADAPTATION & VARIATION
━━━━━━━━━━━━━━━━━━━━━━
- Variation prompts may change per question.
- Follow-up cards must change per question.
- On retakes or focus-area tests:
  • Use different wording
  • Same logic, same rule
  • Never repeat prior cards verbatim

━━━━━━━━━━━━━━━━━━━━━━
FAIL-SAFE RULES
━━━━━━━━━━━━━━━━━━━━━━
If at any point:
- Cards would be generic
- Cards would repeat
- Logic would not be question-specific

→ SKIP follow-ups
→ Deliver take-home rule
→ Move on

━━━━━━━━━━━━━━━━━━━━━━
NON-NEGOTIABLE
━━━━━━━━━━━━━━━━━━━━━━
- Never exceed 2 follow-up turns.
- Never explain after the take-home rule.
- Never reuse cards across questions.
- Never sound generic.

You are not a chatbot.
You are an exam coach.`;

// PROMPT MATCH: Greeting helper methods
export const getGreetingForNewUser = (language: string): string => {
  if (language === 'nl') return 'Hallo! Ik ben TheoryCoach. Laten we beginnen!';
  if (language === 'ar') return 'مرحباً! أنا TheoryCoach. لنبدأ!';
  return 'Hi! I am TheoryCoach. Let\'s get started!';
};

export const getGreetingForReturningUser = (language: string): string => {
  if (language === 'nl') return 'Welkom terug! Laten we doorgaan waar je was gebleven.';
  if (language === 'ar') return 'مرحباً بعودتك! دعنا نكمل من حيث توقفت.';
  return 'Welcome back! Let\'s continue from where you left off.';
};

export const getGreetingCard = (language: string): string => {
  if (language === 'nl') return 'Laten we gaan!';
  if (language === 'ar') return 'هيا بنا!';
  return 'Let\'s go!';
};

export const getContinueCard = (language: string): string => {
  if (language === 'nl') return 'Doorgaan';
  if (language === 'ar') return 'متابعة';
  return 'Continue';
};

// Prompt helper methods
export const getFollowUpHelper = (language: string): { title: string; subtitle: string } => {
  if (language === 'nl') {
    return {
      title: 'Vraag TheoryCoach over deze vraag',
      subtitle: 'Houd je vraag kort en gericht op deze vraag.'
    };
  }
  if (language === 'ar') {
    return {
      title: 'اسأل TheoryCoach عن هذا السؤال',
      subtitle: 'اجعل سؤالك قصيرًا ومحددًا لهذه المسألة.'
    };
  }
  return {
    title: 'Ask TheoryCoach about this question',
    subtitle: 'Keep it short and focused on this question.'
  };
};

export const getAnswerFirstPrompt = (language: string): string => {
  if (language === 'nl') return 'Beantwoord eerst de vraag, dan leg ik het uit.';
  if (language === 'ar') return 'أجب أولاً على السؤال، ثم سأشرح.';
  return 'Answer the question first, then I can explain.';
};

export const getThinkingPrompt = (language: string): string => {
  if (language === 'nl') return 'Even denken...';
  if (language === 'ar') return 'لحظة من فضلك...';
  return 'Thinking...';
};

export const getProceedPrompt = (language: string): string => {
  // Final message only - asks to move on after coaching
  if (language === 'nl') return 'Laten we doorgaan naar de volgende vraag.';
  if (language === 'ar') return 'دعنا ننتقل إلى السؤال التالي.';
  return 'Let\'s move on to the next question.';
};

export const getCorrectConfirmation = (language: string, explanation?: string): string => {
  // PROMPT MATCH: "Correct!" + explanation (1–2 sentences)
  const conciseExplanation = explanation 
    ? explanation.split(/[.!?؟]/).slice(0, 2).join('. ').trim() + '.'
    : '';
  
  if (language === 'nl') {
    return conciseExplanation 
      ? `Goed gedaan. Correct. ${conciseExplanation}`
      : 'Goed gedaan. Correct.';
  }
  if (language === 'ar') {
    return conciseExplanation
      ? `أحسنت. صحيح. ${conciseExplanation}`
      : 'أحسنت. صحيح.';
  }
  return conciseExplanation
    ? `Nice work. Correct. ${conciseExplanation}`
    : 'Nice work. Correct.';
};

export const getProactiveFollowUpPrompt = (language: string): string => {
  if (language === 'nl') return 'Wat maakte deze optie logisch voor je?';
  if (language === 'ar') return 'ما الذي جعلك تختار هذا الخيار؟';
  return 'What made this option feel right?';
};

// PROMPT MATCH: Variation prompts - empathetic reflective prompts after wrong answers
// Randomly selects a prompt each time - varies even on retakes of the same question
// Tone: Empathetic, acknowledging common mistakes, encouraging
export const getVariationPrompt = (questionIndex: number, sessionId: string, language: string = 'en'): string => {
  // Random selection - different prompt each time, even on retakes
  const index = Math.floor(Math.random() * 6);
  
  if (language === 'nl') {
    const prompts = [
      'Dit is een lastige - laten we het samen bekijken.',
      'Geen zorgen, dit is verwarrend. Laten we het uitzoeken.',
      'Dit is een veelgemaakte fout. Wat maakte je onzeker?',
      'Laten we dit samen begrijpen.',
      'Hier struikelen veel mensen over. Wat vond je lastig?',
      'Samen komen we er wel uit. Waar wil je op terugkijken?'
    ];
    return prompts[index];
  }
  if (language === 'ar') {
    const prompts = [
      'هذا صعب - لنراجعه معاً.',
      'لا تقلق، هذا محير. لنفهمه معاً.',
      'هذا خطأ شائع. ما الذي أربكك؟',
      'لنفهم هذا معاً.',
      'كثيرون يجدون هذا صعباً. ما الذي وجدته غامضاً؟',
      'معاً سنفهم. ما الذي تريد المراجعة عليه؟'
    ];
    return prompts[index];
  }
  // English prompts - empathetic, encouraging (no blame)
  const prompts = [
    'This one is tricky - let\'s work through it.',
    'No worries, this is confusing. Let\'s figure it out.',
    'This is a common mistake. What made you unsure?',
    'Let\'s understand this together.',
    'This one trips people up. What felt unclear to you?',
    'We\'ll get there. What would you like to look at again?'
  ];
  return prompts[index];
};

// Deterministic prompt selection to keep voice + UI in sync per question
export const getVariationPromptStable = (
  questionId: string,
  sessionId: string,
  language: string = 'en'
): string => {
  const promptsByLang: Record<string, string[]> = {
    nl: [
      'Dit is een lastige - laten we het samen bekijken.',
      'Geen zorgen, dit is verwarrend. Laten we het uitzoeken.',
      'Dit is een veelgemaakte fout. Wat maakte je onzeker?',
      'Laten we dit samen begrijpen.',
      'Hier struikelen veel mensen over. Wat vond je lastig?',
      'Samen komen we er wel uit. Waar wil je op terugkijken?'
    ],
    ar: [
      'هذا صعب - لنراجعه معاً.',
      'لا تقلق، هذا محير. لنفهمه معاً.',
      'هذا خطأ شائع. ما الذي أربكك؟',
      'لنفهم هذا معاً.',
      'كثيرون يجدون هذا صعباً. ما الذي وجدته غامضاً؟',
      'معاً سنفهم. ما الذي تريد المراجعة عليه؟'
    ],
    en: [
      'This one is tricky - let\'s work through it.',
      'No worries, this is confusing. Let\'s figure it out.',
      'This is a common mistake. What made you unsure?',
      'Let\'s understand this together.',
      'This one trips people up. What felt unclear to you?',
      'We\'ll get there. What would you like to look at again?'
    ]
  };

  const prompts = promptsByLang[language] || promptsByLang.en;
  const seed = `${sessionId}:${questionId}:${language}`;
  let hash = 0;
  for (let i = 0; i < seed.length; i++) {
    hash = (hash << 5) - hash + seed.charCodeAt(i);
    hash |= 0;
  }
  const index = Math.abs(hash) % prompts.length;
  return prompts[index];
};

export const getMistakeRecapLabels = (language: string): {
  confusedLabel: string;
  keyRuleLabel: string;
  memoryHookLabel: string;
} => {
  if (language === 'nl') {
    return {
      confusedLabel: 'Wat je verwarde',
      keyRuleLabel: 'Belangrijkste regel',
      memoryHookLabel: 'Onthoud dit'
    };
  }
  if (language === 'ar') {
    return {
      confusedLabel: 'ما الذي أربكك',
      keyRuleLabel: 'القاعدة الأساسية',
      memoryHookLabel: 'تذكّر هذا'
    };
  }
  return {
    confusedLabel: 'What you confused',
    keyRuleLabel: 'Key rule',
    memoryHookLabel: 'Remember this'
  };
};

export const getKeyRuleLine = (language: string, ruleSnippet: string): string => {
  const labels = getMistakeRecapLabels(language);
  if (!ruleSnippet) return '';
  return `${labels.keyRuleLabel}: ${ruleSnippet}`;
};

export const getMemoryHookLine = (language: string, ruleSnippet: string): string => {
  const labels = getMistakeRecapLabels(language);
  if (!ruleSnippet) return '';
  return `${labels.memoryHookLabel}: ${ruleSnippet}`;
};

export const getReflectionPrompt = (language: string): string => {
  if (language === 'nl') {
    const prompts = [
      'Wat maakte deze optie logisch voor je?',
      'Welke regel heb je hier toegepast?',
      'Wat zou je de volgende keer anders doen?'
    ];
    return prompts[Math.floor(Math.random() * prompts.length)];
  }
  if (language === 'ar') {
    const prompts = [
      'ما الذي جعلك تختار هذا الخيار؟',
      'أي قاعدة طبقتها هنا؟',
      'ماذا ستفعل بشكل مختلف المرة القادمة؟'
    ];
    return prompts[Math.floor(Math.random() * prompts.length)];
  }
  const prompts = [
    'What made this option feel right?',
    'Which rule did you apply here?',
    'What will you do differently next time?'
  ];
  return prompts[Math.floor(Math.random() * prompts.length)];
};

export const getClarifyPrompt = (language: string): string => {
  if (language === 'nl') {
    const prompts = [
      'Kun je aangeven welk deel van de vraag of uitleg onduidelijk is?',
      'Bedoel je de regel of een antwoordoptie?',
      'Kun je je vraag iets specifieker maken?'
    ];
    return prompts[Math.floor(Math.random() * prompts.length)];
  }
  if (language === 'ar') {
    const prompts = [
      'هل يمكنك تحديد أي جزء من السؤال أو الشرح غير واضح؟',
      'هل تقصد القاعدة أم خيار الإجابة؟',
      'هل يمكنك جعل سؤالك أكثر تحديداً؟'
    ];
    return prompts[Math.floor(Math.random() * prompts.length)];
  }
  const prompts = [
    'I\'m not sure what you\'re asking yet. Can you tell me which part of the question or explanation you\'re unsure about?',
    'Do you mean the rule or an answer option?'
  ];
  return prompts[Math.floor(Math.random() * prompts.length)];
};

export const getReflectionSuggestions = (language: string): string[] => {
  if (language === 'nl') {
    return [
      'Wat maakte deze optie logisch voor je?',
      'Welke regel heb je hier toegepast?',
      'Wat zou je de volgende keer anders doen?'
    ];
  }
  if (language === 'ar') {
    return [
      'ما الذي جعلك تختار هذا الخيار؟',
      'أي قاعدة طبقتها هنا؟',
      'ماذا ستفعل بشكل مختلف المرة القادمة؟'
    ];
  }
  return [
    'What made this option feel right?',
    'Which rule did you apply here?',
    'What will you do differently next time?'
  ];
};

export const getLastQuestionPrompt = (language: string): string => {
  if (language === 'nl') return 'Dit is de laatste vraag. Beantwoord om af te ronden.';
  if (language === 'ar') return 'هذا هو السؤال الأخير. أجب للإنهاء.';
  return 'This is the last question. Answer it to finish.';
};

export const getErrorPrompt = (language: string): string => {
  if (language === 'nl') return 'Sorry, er ging iets mis. Probeer het opnieuw.';
  if (language === 'ar') return 'عذراً، حدث خطأ. حاول مرة أخرى.';
  return 'Sorry, something went wrong. Please try again.';
};

export const getInputPlaceholder = (language: string): string => {
  if (language === 'nl') return 'Ask TheoryCoach (3 questions left for this question)';
  if (language === 'ar') return 'Ask TheoryCoach (3 questions left for this question)';
  return 'Ask TheoryCoach (3 questions left for this question)';
};

export const getDisclaimer = (language: string): string => {
  if (language === 'nl') return 'TheoryCoach kan fouten maken. Controleer belangrijke info.';
  if (language === 'ar') return 'قد يخطئ TheoryCoach. تحقّق من المعلومات المهمة.';
  return 'TheoryCoach can make mistakes. Check important info.';
};

export const getNextStepText = (language: string): string => {
  if (language === 'nl') return 'Volgende stap: oefen nog 2 vragen.';
  if (language === 'ar') return 'الخطوة التالية: تدرب على سؤالين إضافيين.';
  return 'Next step: practice 2 more questions.';
};
